import {
  type FeaturedThreadResPagedData,
  forumsV1FeaturedThreadsList,
  type TrendingListItemResponseTrendingListResponseRelatedPagedData,
} from '@on3/api';
import { type IUser } from '@on3/ui-lib/api/schema/custom-contracts';
import { type SiteServerProps } from '@on3/ui-lib/contexts/SiteProvider';
import withAuthentication from '@on3/ui-lib/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/hocs/withValidUrl';
import { proxyApi } from '@on3/ui-lib/index';
import { getFulfilledValue } from '@on3/ui-lib/utils/api';
import { ExcoPlayer } from '@public/components/Ads/ExcoPlayer/ExcoPlayer';
import { AdSidebarLayout } from '@public/components/Layouts/AdSidebarLayout';
import { PageWrapper } from '@public/components/Layouts/PageWrapper';
import { MegaBoard } from '@public/components/MegaBoard';
import { TopHeadlines } from '@public/components/TopHeadlines';
import { createCanonicalUrl } from '@public/utils/meta';
import { siteModules } from '@public/utils/sites';
import { type GetServerSideProps } from 'next';

interface IMegaboardPageProps {
  siteData: SiteServerProps;
  userData: IUser | null;
  trendingList: TrendingListItemResponseTrendingListResponseRelatedPagedData | null;
  threadData: FeaturedThreadResPagedData | null;
}

const MegaboardPage = ({
  siteData,
  trendingList,
  threadData,
}: IMegaboardPageProps) => {
  const canonicalUrl = createCanonicalUrl('megaboard');
  const title = 'MegaBoard | On3.com';
  const description =
    "The Community of College Sports — Selected from On3's extensive fan site message boards.";

  return (
    <PageWrapper
      canonical={canonicalUrl}
      description={description}
      siteData={siteData}
      title={title}
    >
      <AdSidebarLayout
        sidebarContent={
          <>
            <ExcoPlayer />
            {trendingList && <TopHeadlines trendingList={trendingList} />}
          </>
        }
        sidebarMobile={false}
      >
        <MegaBoard threadData={threadData} />
      </AdSidebarLayout>
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  const { page } = ctx.query as { page?: string };

  if (!siteData) return { notFound: true };

  try {
    const [threadReq, trendingListReq] = await Promise.allSettled([
      forumsV1FeaturedThreadsList(proxyApi, { page: page ? Number(page) : 1 }),
      siteModules.trendingList(proxyApi, { trendingListKey: 1 }),
    ]);

    return {
      props: {
        siteData,
        userData,
        threadData: getFulfilledValue(threadReq),
        trendingList: getFulfilledValue(trendingListReq),
      },
    };
  } catch (error) {
    console.error('Error fetching server-side props:', error);

    return {
      props: {
        siteData: null,
        userData: null,
      },
    };
  }
};

export default withValidUrl(MegaboardPage);
